/**
 * PATH: packages/drafting/src/canvas/entities/EntityShape.tsx
 * FILE_ID: DRAFTING-CANVAS-ENTITYSHAPE-001
 * PURPOSE: Render ONE Drawing-IR entity with react-konva + handle its
 *   move interaction. One renderer per entity type, dispatched here.
 * WHY: The renderer is a swappable consumer of the IR (scope §4). Keeping
 *   per-entity rendering isolated keeps DraftCanvas thin and makes each
 *   shape type independently reasoned about.
 */
import { Line, Rect, Circle, Text, Arrow, Group } from 'react-konva'
import type Konva from 'konva'
import type { Entity, Layer, Point } from '../../ir/schema'

export interface EntityShapeProps {
  entity: Entity
  layer?: Layer
  selected: boolean
  draggable: boolean
  onSelect: (id: string) => void
  onChange: (next: Entity) => void
}

const DEFAULT_STROKE = '#2c3a47'

function resolveStroke(e: Entity, layer?: Layer): string {
  return e.stroke ?? layer?.stroke ?? DEFAULT_STROKE
}

function resolveFill(e: Entity, layer?: Layer): string | undefined {
  const raw = e.fill ?? layer?.color
  if (!raw || raw === 'none') return undefined
  // P1: treat "hatch:xyz" fills as a flat tint (real hatch patterns = P2).
  if (raw.startsWith('hatch:')) return layer?.color && layer.color !== 'none' ? layer.color : undefined
  return raw
}

const flat = (pts: Point[]): number[] => pts.flat()

function translatePoints(pts: Point[], dx: number, dy: number): Point[] {
  return pts.map(([x, y]) => [x + dx, y + dy] as Point)
}

/** Common Konva props shared by every shape. */
function common(e: Entity, layer: Layer | undefined, selected: boolean, draggable: boolean, onSelect: (id: string) => void) {
  return {
    id: e.id,
    name: e.id,
    stroke: resolveStroke(e, layer),
    strokeWidth: e.strokeWidth ?? 1,
    dash: e.dash ?? layer?.dash,
    opacity: e.opacity ?? 1,
    strokeScaleEnabled: false,
    shadowColor: selected ? '#2563eb' : undefined,
    shadowBlur: selected ? 8 : 0,
    shadowOpacity: selected ? 0.9 : 0,
    draggable,
    onClick: () => onSelect(e.id),
    onTap: () => onSelect(e.id),
  }
}

/** Bake a point-based shape's drag delta back into IR coords, reset node. */
function pointDragEnd(node: Konva.Node): { dx: number; dy: number } {
  const dx = node.x()
  const dy = node.y()
  node.position({ x: 0, y: 0 })
  return { dx, dy }
}

export function EntityShape(props: EntityShapeProps) {
  const { entity: e, layer, selected, draggable, onSelect, onChange } = props
  const base = common(e, layer, selected, draggable, onSelect)
  const fill = resolveFill(e, layer)

  switch (e.type) {
    case 'line':
      return (
        <Line
          {...base}
          points={[e.a[0], e.a[1], e.b[0], e.b[1]]}
          onDragEnd={(ev) => {
            const { dx, dy } = pointDragEnd(ev.target)
            onChange({ ...e, a: [e.a[0] + dx, e.a[1] + dy], b: [e.b[0] + dx, e.b[1] + dy] })
          }}
        />
      )
    case 'polyline':
    case 'hatch':
      return (
        <Line
          {...base}
          points={flat(e.points)}
          closed={e.type === 'hatch' ? true : e.closed}
          fill={e.type === 'hatch' ? fill ?? 'rgba(44,58,71,0.12)' : fill}
          onDragEnd={(ev) => {
            const { dx, dy } = pointDragEnd(ev.target)
            onChange({ ...e, points: translatePoints(e.points, dx, dy) })
          }}
        />
      )
    case 'rect':
      return (
        <Rect
          {...base}
          x={e.x}
          y={e.y}
          width={e.w}
          height={e.h}
          fill={fill}
          onDragEnd={(ev) => onChange({ ...e, x: ev.target.x(), y: ev.target.y() })}
        />
      )
    case 'circle':
      return (
        <Circle
          {...base}
          x={e.cx}
          y={e.cy}
          radius={e.r}
          fill={fill}
          onDragEnd={(ev) => onChange({ ...e, cx: ev.target.x(), cy: ev.target.y() })}
        />
      )
    case 'arc':
      // P1: approximate an arc as a full circle outline (true arc = P2).
      return (
        <Circle
          {...base}
          x={e.cx}
          y={e.cy}
          radius={e.r}
          onDragEnd={(ev) => onChange({ ...e, cx: ev.target.x(), cy: ev.target.y() })}
        />
      )
    case 'text':
      return (
        <Text
          {...base}
          x={e.at[0]}
          y={e.at[1]}
          text={e.text}
          fontSize={e.size ?? 12}
          rotation={e.rotation ?? 0}
          fill={resolveStroke(e, layer)}
          onDragEnd={(ev) => onChange({ ...e, at: [ev.target.x(), ev.target.y()] })}
        />
      )
    case 'dimension': {
      const mid: Point = [(e.a[0] + e.b[0]) / 2, (e.a[1] + e.b[1]) / 2]
      const label = e.value != null ? `${e.value}${e.unit ?? ''}` : ''
      return (
        <Group
          id={e.id}
          name={e.id}
          draggable={draggable}
          onClick={() => onSelect(e.id)}
          onTap={() => onSelect(e.id)}
          onDragEnd={(ev) => {
            const { dx, dy } = pointDragEnd(ev.target)
            onChange({ ...e, a: [e.a[0] + dx, e.a[1] + dy], b: [e.b[0] + dx, e.b[1] + dy] })
          }}
        >
          <Arrow
            points={[e.a[0], e.a[1], e.b[0], e.b[1]]}
            stroke={resolveStroke(e, layer)}
            fill={resolveStroke(e, layer)}
            strokeWidth={e.strokeWidth ?? 1}
            pointerAtBeginning
            pointerLength={6}
            pointerWidth={6}
            strokeScaleEnabled={false}
          />
          {label ? (
            <Text x={mid[0]} y={mid[1] - (e.offset ?? 8)} text={label} fontSize={11} fill={resolveStroke(e, layer)} />
          ) : null}
        </Group>
      )
    }
    case 'leader':
      return (
        <Group
          id={e.id}
          name={e.id}
          draggable={draggable}
          onClick={() => onSelect(e.id)}
          onTap={() => onSelect(e.id)}
          onDragEnd={(ev) => {
            const { dx, dy } = pointDragEnd(ev.target)
            onChange({ ...e, from: [e.from[0] + dx, e.from[1] + dy], to: [e.to[0] + dx, e.to[1] + dy] })
          }}
        >
          <Arrow
            points={[e.from[0], e.from[1], e.to[0], e.to[1]]}
            stroke={resolveStroke(e, layer)}
            fill={resolveStroke(e, layer)}
            strokeWidth={e.strokeWidth ?? 1}
            pointerLength={6}
            pointerWidth={6}
            strokeScaleEnabled={false}
          />
          <Text x={e.to[0] + 4} y={e.to[1] - 6} text={e.text} fontSize={11} fill={resolveStroke(e, layer)} />
        </Group>
      )
    case 'group':
      return null
  }
}
